/**
 * ðŸ”½ Dropdown Component Usage Example
 *
 * Props:
 * - options: Array<{ value: string, label: string }>
 * - isMulti: boolean (default false)
 * - onSelect: function (callback with selected value(s))
 * - selected:
 *    - single mode: { value, label }
 *    - multi mode: array of { value, label }
 *    - NEW: you can also pass an array of values (e.g., ["uniqid", "uniqueid"])
 *           and matching options will be auto-selected.
 * - size: "sm" | "md" (default "md")
 * - allowCreate: boolean â€” single-select only; type in Search to add a new value
 *   immediately (shows Add "â€¦"). onSelect receives { value, label, __created: true }.
 *
 * Example 1: Single Select
 * --------------------------------
 * const roles = [
 *   { value: "admin", label: "Admin" },
 *   { value: "user", label: "User" },
 *   { value: "manager", label: "Manager" },
 * ];
 *
 * <Dropdown
 *   options={roles}
 *   onSelect={(val) => console.log(val)}
 *   selected={{ value: "user", label: "User" }}
 * />
 *
 * Example 2: Multi Select (preselect by array of objects)
 * --------------------------------
 * <Dropdown
 *   options={roles}
 *   isMulti={true}
 *   onSelect={(vals) => console.log(vals)}
 *   selected={[
 *     { value: "admin", label: "Admin" },
 *     { value: "manager", label: "Manager" },
 *   ]}
 * />
 *
 * Example 3: Multi Select (preselect by array of values)
 * --------------------------------
 * <Dropdown
 *   options={roles}
 *   isMulti={true}
 *   onSelect={(vals) => console.log(vals)}
 *   selected={["admin", "manager"]}
 * />
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import "./DropDown.scss";

const Dropdown = ({
  isColorDropDown = false,
  options = [],
  isMulti = false,
  onSelect,
  selected = [],
  showSearch = true,
  disabled = false,
  showMultiSelectedTooltip = false,
  size = "md",
  /** When true (single-select), search can create a new option immediately. */
  allowCreate = false,
  /** Option values always kept in the filtered list (e.g. ["Other"]). */
  pinValues = [],
  /** Extra class on root + portal menu wrapper (e.g. compact import grids). */
  className = "",
  /** Open menu can be wider than the toggle (px). */
  menuMinWidth = 0,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedItems, setSelectedItems] = useState(
    isMulti ? selected : selected || null
  );
  const dropdownRef = useRef(null);
  const menuRef = useRef(null);
  const [menuPos, setMenuPos] = useState({});

  // ðŸ”¹ Normalize selected if it comes as array of values
  useEffect(() => {
    if (isMulti && Array.isArray(selected)) {
      if (selected.length === 0) {
        setSelectedItems([]);
      } else if (typeof selected[0] === "string") {
        const mapped = options.filter((opt) => selected.includes(opt.value));
        setSelectedItems(mapped);
      } else {
        setSelectedItems(selected);
      }
    } else if (!isMulti && selected?.value != null) {
      setSelectedItems(selected);
    } else if (!isMulti) {
      setSelectedItems(null);
    }
  }, [selected, options, isMulti]);

  const updatePos = useCallback(() => {
    if (!dropdownRef.current) return;
    const EDGE_GAP = 8;
    const PREFERRED_MENU_HEIGHT = 320;
    const MIN_MENU_HEIGHT = 140;
    const r = dropdownRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - r.bottom - EDGE_GAP;
    const spaceAbove = r.top - EDGE_GAP;
    let flip = spaceBelow < 220 && spaceAbove > spaceBelow;
    let usableSpace = flip ? spaceAbove : spaceBelow;

    // If the chosen side is too tight, prefer the side with more space.
    if (usableSpace < MIN_MENU_HEIGHT) {
      flip = spaceAbove > spaceBelow;
      usableSpace = Math.max(spaceAbove, spaceBelow);
    }

    const menuMaxHeight = Math.max(
      MIN_MENU_HEIGHT,
      Math.min(PREFERRED_MENU_HEIGHT, usableSpace)
    );

    setMenuPos({
      position: "fixed",
      left: r.left,
      width: r.width,
      zIndex: 9999,
      maxHeight: menuMaxHeight,
      ...(flip
        ? { bottom: window.innerHeight - r.top + 4 }
        : { top: r.bottom + 4 }),
    });
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const inToggle = dropdownRef.current?.contains(event.target);
      const inMenu = menuRef.current?.contains(event.target);
      if (!inToggle && !inMenu) {
        setIsOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    updatePos();
    const reposition = () => updatePos();
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [isOpen, updatePos]);

  const toggleDropdown = () => {
    if (disabled) return;
    if (!isOpen) {
      updatePos();
      setSearch("");
    }
    const next = !isOpen;
    setIsOpen(next);
    if (next) {
      window.requestAnimationFrame(updatePos);
      window.setTimeout(updatePos, 120);
    }
  };

  const handleSelect = (option) => {
    if (disabled) return;
    if (option?.disabled) return;
    if (isMulti) {
      const exists = selectedItems.find((item) => item.value === option.value);
      const updated = exists
        ? selectedItems.filter((item) => item.value !== option.value)
        : [...selectedItems, option];
      setSelectedItems(updated);
      onSelect && onSelect(updated);
    } else {
      setSelectedItems(option);
      onSelect && onSelect(option);
      setIsOpen(false);
      setSearch("");
    }
  };

  const isSelected = (option) => {
    return isMulti
      ? selectedItems.find((item) => item.value === option.value)
      : selectedItems?.value === option.value;
  };

  const searchLower = String(search ?? "").toLowerCase();
  const pinSet = new Set(
    (Array.isArray(pinValues) ? pinValues : [])
      .map((v) => String(v ?? "").trim().toLowerCase())
      .filter(Boolean),
  );
  const isPinnedOption = (opt) =>
    pinSet.has(String(opt?.value ?? "").trim().toLowerCase());

  let filteredOptions = !searchLower
    ? options
    : options.filter((opt, index) => {
        if (isPinnedOption(opt)) return true;
        const label = String(opt?.label ?? "").toLowerCase();
        if (label.includes(searchLower)) return true;
        // Keep a group header if any following selectable option matches.
        if (!opt?.disabled) return false;
        for (let i = index + 1; i < options.length; i += 1) {
          const next = options[i];
          if (next?.disabled) break;
          if (isPinnedOption(next)) return true;
          if (
            String(next?.label ?? "")
              .toLowerCase()
              .includes(searchLower)
          ) {
            return true;
          }
        }
        return false;
      });

  // When search matches nothing else, still show pinned options (e.g. Other).
  if (searchLower && pinSet.size > 0) {
    const hasSelectableNonPin = filteredOptions.some(
      (opt) => !opt?.disabled && !isPinnedOption(opt),
    );
    const pinnedOpts = options.filter(
      (opt) => !opt?.disabled && isPinnedOption(opt),
    );
    if (!hasSelectableNonPin && pinnedOpts.length) {
      filteredOptions = pinnedOpts;
    } else {
      const present = new Set(
        filteredOptions.map((o) => String(o?.value ?? "").toLowerCase()),
      );
      pinnedOpts.forEach((opt) => {
        const key = String(opt?.value ?? "").toLowerCase();
        if (!present.has(key)) filteredOptions = [...filteredOptions, opt];
      });
    }
  }

  const selectableFilteredOptions = filteredOptions.filter((opt) => !opt.disabled);

  const createQuery = String(search ?? "").trim();
  const createQueryLower = createQuery.toLowerCase();
  const canCreate =
    allowCreate &&
    !isMulti &&
    !disabled &&
    createQuery.length > 0 &&
    !options.some((opt) => {
      if (opt?.disabled) return false;
      return (
        String(opt?.value ?? "").toLowerCase() === createQueryLower ||
        String(opt?.label ?? "").toLowerCase() === createQueryLower
      );
    });

  const handleCreateOption = () => {
    if (!canCreate) return;
    handleSelect({
      value: createQuery,
      label: createQuery,
      __created: true,
    });
  };

  const handleSelectAll = () => {
    if (disabled) return;
    const selectableFiltered = selectableFilteredOptions;
    const allSelected = selectableFiltered.every((opt) =>
      selectedItems.find((item) => item.value === opt.value)
    );
    const updated = allSelected
      ? selectedItems.filter(
          (item) =>
            !selectableFiltered.find((opt) => opt.value === item.value),
        )
      : [...new Set([...selectedItems, ...selectableFiltered])];
    setSelectedItems(updated);
    onSelect && onSelect(updated);
  };

  const renderSelectedText = () => {
    if (!isMulti) return selectedItems?.label || "Select...";
    if (!Array.isArray(selectedItems) || selectedItems.length === 0)
      return "Select...";
    if (selectedItems.length > 2) return `${selectedItems.length} selected`;
    return selectedItems
      .map((s) =>
        s.label.length > 15 ? s.label.slice(0, 15) + "..." : s.label
      )
      .join(", ");
  };

  const multiSelectedTooltip =
    showMultiSelectedTooltip &&
    isMulti &&
    Array.isArray(selectedItems) &&
    selectedItems.length > 0
      ? selectedItems.map((item) => item.label).join(", ")
      : undefined;

  const listMaxHeight =
    typeof menuPos.maxHeight === "number"
      ? Math.max(96, menuPos.maxHeight - (showSearch ? 64 : 16))
      : 192;

  const sizeClass =
    size === "sm"
      ? " custom-dropdown--sm"
      : size === "md"
        ? " custom-dropdown--md"
        : "";
  const extraClass = className ? ` ${className}` : "";

  return (
    <div
      className={`custom-dropdown${disabled ? " is-disabled" : ""}${sizeClass}${extraClass}`}
      ref={dropdownRef}
    >
      <div
        className="dropdown-toggle text-truncate"
        onClick={toggleDropdown}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        title={multiSelectedTooltip}
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggleDropdown();
          }
        }}
      >
        <span>
          {isColorDropDown && selectedItems?.value && (
            <span
              style={{
                width: 14,
                height: 14,
                borderRadius: "50%",
                backgroundColor: selectedItems.value,
                display: "inline-block",
                marginRight: 8,
              }}
            ></span>
          )}
          {renderSelectedText()}
        </span>
        <i className="fa fa-chevron-down dropdown-icon ms-2" />
      </div>

      {isOpen &&
        ReactDOM.createPortal(
          <div
            className={`custom-dropdown${sizeClass}${extraClass}`}
            ref={menuRef}
            style={menuPos}
          >
            <div
              className="dropdown-menu"
              style={{
                position: "static",
                display: "block",
                width: "100%",
                maxHeight: menuPos.maxHeight,
                marginTop: 0,
                marginBottom: 0,
                top: "auto",
                bottom: "auto",
              }}
            >
              {showSearch && (
                <input
                  type="text"
                  className="form-control search-box"
                  placeholder="Search..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  autoFocus
                />
              )}
              <ul className="dropdown-list" style={{ maxHeight: listMaxHeight }}>
                {isMulti && filteredOptions.length > 0 && (
                  <li
                    className="dropdown-item select-all"
                    onClick={handleSelectAll}
                  >
                    <input
                      type="checkbox"
                      tabIndex={-1}
                      readOnly
                      checked={
                        selectableFilteredOptions.length > 0 &&
                        selectableFilteredOptions.every((opt) =>
                          selectedItems.find((item) => item.value === opt.value),
                        )
                      }
                    />
                    <span className="ms-2">Select All</span>
                  </li>
                )}
                {filteredOptions.length ? (
                  filteredOptions.map((option) => (
                    <li
                      key={option.value}
                      className={`dropdown-item ${
                        option.disabled ? "is-option-disabled" : ""
                      } ${isSelected(option) ? "selected" : ""}`}
                      onClick={() => handleSelect(option)}
                      aria-disabled={option.disabled ? true : undefined}
                    >
                      {isMulti && (
                        <input
                          type="checkbox"
                          tabIndex={-1}
                          readOnly
                          checked={!!isSelected(option)}
                        />
                      )}
                      {isColorDropDown && (
                        <span
                          style={{
                            width: 14,
                            height: 14,
                            borderRadius: "50%",
                            backgroundColor: option.value,
                            display: "inline-block",
                          }}
                        ></span>
                      )}
                      <span className="ms-2 text-truncate">
                        {option.label}
                      </span>
                    </li>
                  ))
                ) : !canCreate ? (
                  <li className="dropdown-item text-muted">No results</li>
                ) : null}
                {canCreate ? (
                  <li
                    className="dropdown-item dropdown-item--create"
                    onClick={handleCreateOption}
                  >
                    <span className="ms-2">
                      Add &ldquo;{createQuery}&rdquo;
                    </span>
                  </li>
                ) : null}
              </ul>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default Dropdown;
